import { Injectable, inject, signal } from '@angular/core';
import { Actions, ofActionDispatched } from '@ngxs/store';
import { UserVacationV2 } from '@wilson/absences';
import { Logout } from '@wilson/auth/core';
import { VacationManagementV2Service } from '@wilson/vacation-management';

const USER_ID_CHUNK_SIZE = 40;

@Injectable({
  providedIn: 'root',
})
export class TimelineVacationBalanceService {
  private readonly vacationManagementV2Service = inject(
    VacationManagementV2Service,
  );
  private readonly balances = signal<Readonly<Record<string, number | null>>>(
    {},
  );
  private readonly loadingUserIds = signal<ReadonlySet<string>>(new Set());
  private readonly loadedUserIds = new Set<string>();
  private readonly inFlightUserIds = new Set<string>();
  private readonly fetchGenerationByUser = new Map<string, number>();
  private readonly actions$ = inject(Actions);
  private loadedYear = new Date().getFullYear();
  private requestGeneration = 0;

  constructor() {
    this.actions$
      .pipe(ofActionDispatched(Logout))
      .subscribe(() => this.reset());
  }

  hasBalance(userId: string): boolean {
    return Object.prototype.hasOwnProperty.call(this.balances(), userId);
  }

  balance(userId: string): number | null {
    return this.balances()[userId] ?? null;
  }

  isLoading(userId: string): boolean {
    return this.loadingUserIds().has(userId);
  }

  reset(): void {
    this.loadedYear = new Date().getFullYear();
    this.clearCachedBalances();
  }

  reloadForVisibleUsers(userIds: string[]): void {
    for (const userId of userIds) {
      if (userId) {
        this.loadedUserIds.delete(userId);
        this.inFlightUserIds.delete(userId);
        this.fetchGenerationByUser.set(
          userId,
          this.fetchGeneration(userId) + 1,
        );
      }
    }
    this.loadForVisibleUsers(userIds);
  }

  loadForVisibleUsers(userIds: string[]): void {
    const year = new Date().getFullYear();
    if (year !== this.loadedYear) {
      this.loadedYear = year;
      this.clearCachedBalances();
    }

    const requestGeneration = this.requestGeneration;

    const missingUserIds = userIds.filter(
      (userId) =>
        !!userId &&
        !this.loadedUserIds.has(userId) &&
        !this.inFlightUserIds.has(userId),
    );
    if (missingUserIds.length) {
      for (const userId of missingUserIds) {
        this.inFlightUserIds.add(userId);
      }
      this.loadingUserIds.update((current) => {
        const next = new Set(current);
        for (const userId of missingUserIds) {
          next.add(userId);
        }
        return next;
      });

      for (
        let index = 0;
        index < missingUserIds.length;
        index += USER_ID_CHUNK_SIZE
      ) {
        const userIdChunk = missingUserIds.slice(
          index,
          index + USER_ID_CHUNK_SIZE,
        );
        const fetchGenerations = new Map(
          userIdChunk.map((userId) => [userId, this.fetchGeneration(userId)]),
        );
        this.vacationManagementV2Service
          .getUsersVacationStatus({
            where: { year, userIds: userIdChunk },
          })
          .subscribe({
            next: (rows) =>
              this.storeRows(
                year,
                userIdChunk,
                rows,
                requestGeneration,
                fetchGenerations,
              ),
            error: () =>
              this.storeRows(
                year,
                userIdChunk,
                [],
                requestGeneration,
                fetchGenerations,
              ),
          });
      }
    }
  }

  private clearCachedBalances(): void {
    this.requestGeneration += 1;
    this.loadedUserIds.clear();
    this.inFlightUserIds.clear();
    this.fetchGenerationByUser.clear();
    this.balances.set({});
    this.loadingUserIds.set(new Set());
  }

  private fetchGeneration(userId: string): number {
    return this.fetchGenerationByUser.get(userId) ?? 0;
  }

  private storeRows(
    year: number,
    requestedUserIds: string[],
    rows: UserVacationV2[],
    requestGeneration: number,
    fetchGenerations: ReadonlyMap<string, number>,
  ): void {
    const isCurrentRequest =
      requestGeneration === this.requestGeneration && year === this.loadedYear;
    const currentUserIds = isCurrentRequest
      ? requestedUserIds.filter(
          (userId) =>
            fetchGenerations.get(userId) === this.fetchGeneration(userId),
        )
      : [];

    if (currentUserIds.length) {
      const currentUserIdSet = new Set(currentUserIds);
      const returnedUserIds = new Set<string>();
      this.balances.update((current) => {
        const next = { ...current };
        for (const row of rows) {
          if (row.userId && currentUserIdSet.has(row.userId)) {
            next[row.userId] = row.balance;
            returnedUserIds.add(row.userId);
            this.loadedUserIds.add(row.userId);
          }
        }
        for (const userId of currentUserIds) {
          if (!returnedUserIds.has(userId)) {
            next[userId] = null;
            this.loadedUserIds.add(userId);
          }
        }
        return next;
      });
      this.clearInFlight(currentUserIds);
    }
  }

  private clearInFlight(userIds: string[]): void {
    for (const userId of userIds) {
      this.inFlightUserIds.delete(userId);
    }
    this.loadingUserIds.update((current) => {
      const next = new Set(current);
      for (const userId of userIds) {
        next.delete(userId);
      }
      return next;
    });
  }
}
